import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Popover,
  Stack,
  Typography,
  alpha,
  keyframes,
} from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import ViewDayRoundedIcon from '@mui/icons-material/ViewDayRounded';
import ViewWeekRoundedIcon from '@mui/icons-material/ViewWeekRounded';
import WbSunnyRoundedIcon from '@mui/icons-material/WbSunnyRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import moment from 'moment';
import { getHallBookings } from '../api';
import { parseHallEventBookingNotes } from '../utils/hallEventForm';

const HALL_ACCENT = '#4ce1b6';
const SECONDARY = '#5b9cf5';
const WARNING = '#f6c453';
const DANGER = '#f07178';
const MUTED = '#94a3b8';

const STATUS_COLORS = {
  tentative: WARNING,
  confirmed: HALL_ACCENT,
  completed: SECONDARY,
  cancelled: DANGER,
};

/** Stable per-hall accent palette so bars are easy to tell apart. */
const HALL_INDICATOR_COLORS = [
  '#4ce1b6',
  '#5b9cf5',
  '#f6c453',
  '#a78bfa',
  '#fb923c',
  '#38bdf8',
  '#f472b6',
  '#34d399',
  '#f07178',
  '#818cf8',
];

function getHallIndicatorColor(hallId, hallName = '') {
  const key = String(hallId != null && hallId !== '' ? hallId : hallName || 'hall');
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i);
    hash |= 0;
  }
  return HALL_INDICATOR_COLORS[Math.abs(hash) % HALL_INDICATOR_COLORS.length];
}

const toolbarShimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const VIEW_OPTIONS = [
  { value: 'month', label: 'Month', icon: CalendarMonthRoundedIcon },
  { value: 'week', label: 'Week', icon: ViewWeekRoundedIcon },
  { value: 'day', label: 'Day', icon: ViewDayRoundedIcon },
];

function formatTimeLabel(value) {
  if (!value) return '—';
  const parsed = moment(value, ['HH:mm:ss', 'HH:mm', moment.ISO_8601], true);
  if (!parsed.isValid()) return String(value).slice(0, 5);
  return parsed.format('hh:mm A');
}

function timeToMinutes(value) {
  const raw = String(value || '00:00').trim();
  const parsed = moment(raw, ['HH:mm:ss', 'HH:mm', 'hh:mm A', 'h:mm A'], true);
  if (parsed.isValid()) {
    return (parsed.hours() * 60) + parsed.minutes();
  }
  const [hour = '0', minute = '0'] = raw.slice(0, 5).split(':');
  return (Number(hour) * 60) + Number(minute || 0);
}

function toDateKey(value) {
  if (!value) return '';
  if (moment.isMoment(value) && value.isValid()) return value.format('YYYY-MM-DD');
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}

function getEventDurationHours(event) {
  const startDate = event?.event_date || event?.date;
  const endDate = event?.end_date || startDate;
  const start = moment(`${startDate} ${event?.start_time || '00:00'}`, 'YYYY-MM-DD HH:mm');
  const end = moment(`${endDate} ${event?.end_time || '00:00'}`, 'YYYY-MM-DD HH:mm');
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return 0;
  return Math.max(0.5, Math.round((end.diff(start, 'minutes') / 60) * 2) / 2);
}

/** Full-day blocks count nights (10AM → next 10AM = 1). Same calendar day = 1. */
function getFullDayBlockCount(event) {
  const start = moment(event?.event_date || event?.date, 'YYYY-MM-DD');
  const end = moment(event?.end_date || event?.event_date || event?.date, 'YYYY-MM-DD');
  if (!start.isValid() || !end.isValid()) return 1;
  return Math.max(1, end.diff(start, 'days'));
}

function eventOverlapsHour(event, dayKey, hourLabel) {
  const hourStart = timeToMinutes(hourLabel);
  const hourEnd = hourStart + 60;
  const eventStartDate = toDateKey(event.event_date);
  const eventEndDate = toDateKey(event.end_date || event.event_date);
  const startMin = timeToMinutes(event.start_time);
  const endMin = timeToMinutes(event.end_time);

  if (dayKey === eventStartDate && dayKey === eventEndDate) {
    return startMin < hourEnd && endMin > hourStart;
  }
  if (dayKey === eventStartDate) {
    return startMin < hourEnd;
  }
  if (dayKey === eventEndDate) {
    return endMin > hourStart;
  }
  return Boolean(eventStartDate && eventEndDate && dayKey > eventStartDate && dayKey < eventEndDate);
}

function getDayViewEventLayout(event, dayKey, rowHeight = 56) {
  const eventStartDate = toDateKey(event.event_date);
  const eventEndDate = toDateKey(event.end_date || event.event_date);
  let startMin = 0;
  let endMin = 24 * 60;

  if (dayKey === eventStartDate && dayKey === eventEndDate) {
    startMin = timeToMinutes(event.start_time);
    endMin = timeToMinutes(event.end_time);
  } else if (dayKey === eventStartDate) {
    startMin = timeToMinutes(event.start_time);
    endMin = 24 * 60;
  } else if (dayKey === eventEndDate) {
    startMin = 0;
    endMin = timeToMinutes(event.end_time);
  }

  if (!Number.isFinite(startMin)) startMin = 0;
  if (!Number.isFinite(endMin) || endMin <= startMin) {
    endMin = startMin + 60;
  }

  const top = (startMin / 60) * rowHeight;
  const height = Math.max(rowHeight * 0.75, ((endMin - startMin) / 60) * rowHeight);
  return { top, height };
}

const MINUTES_PER_DAY = 24 * 60;

function formatMinutesLabel(minutes) {
  const clamped = Math.max(0, Math.min(MINUTES_PER_DAY, Number(minutes) || 0));
  if (clamped >= MINUTES_PER_DAY) return '12:00 AM';
  return moment().startOf('day').add(clamped, 'minutes').format('hh:mm A');
}

/**
 * Portion of a calendar day occupied by an event (for month/week bars).
 * Example: 13th 10AM → 14th 10AM → 13th bar from ~42%–100%, 14th bar from 0%–42%.
 */
function getEventDaySegment(event, dayKey) {
  const startKey = toDateKey(event?.event_date || event?.date);
  const endKey = toDateKey(event?.end_date || event?.event_date || event?.date) || startKey;
  if (!startKey || !dayKey || dayKey < startKey || dayKey > endKey) return null;

  const startMin = Math.max(0, Math.min(MINUTES_PER_DAY, timeToMinutes(event?.start_time)));
  const endMinRaw = timeToMinutes(event?.end_time);
  const endMin = Math.max(0, Math.min(MINUTES_PER_DAY, Number.isFinite(endMinRaw) ? endMinRaw : 0));

  // Ends exactly at midnight on a later day → no footprint on that end date.
  if (dayKey === endKey && dayKey !== startKey && endMin <= 0) return null;

  let segStart = 0;
  let segEnd = MINUTES_PER_DAY;

  if (dayKey === startKey && dayKey === endKey) {
    segStart = startMin;
    segEnd = endMin > startMin ? endMin : Math.min(MINUTES_PER_DAY, startMin + 60);
  } else if (dayKey === startKey) {
    segStart = startMin;
    segEnd = MINUTES_PER_DAY;
  } else if (dayKey === endKey) {
    segStart = 0;
    segEnd = endMin > 0 ? endMin : MINUTES_PER_DAY;
  }

  if (segEnd <= segStart) return null;

  const leftPct = (segStart / MINUTES_PER_DAY) * 100;
  const rawWidthPct = ((segEnd - segStart) / MINUTES_PER_DAY) * 100;
  // Keep short overnight tails visible without overflowing the cell.
  const widthPct = Math.min(100 - leftPct, Math.max(10, rawWidthPct));
  const isPartial = leftPct > 0.5 || (leftPct + widthPct) < 99.5;

  const isStartDay = dayKey === startKey;
  const isEndDay = dayKey === endKey;
  return {
    leftPct,
    widthPct,
    segStart,
    segEnd,
    isPartial,
    isStartDay,
    isEndDay,
    continuesLeft: !isStartDay,
    continuesRight: !isEndDay,
    timeLabel: `${formatMinutesLabel(segStart)} – ${formatMinutesLabel(segEnd)}`,
  };
}

function resolveHallImageUrl(hall) {
  if (!hall) return '';
  return (
    hall.thumbnail
    || hall.hall_image_url
    || (Array.isArray(hall.hall_images) && hall.hall_images[0]?.url)
    || ''
  );
}

function buildHallImageLookup(halls = []) {
  const map = {};
  halls.forEach((hall) => {
    if (hall?.id == null) return;
    map[String(hall.id)] = resolveHallImageUrl(hall);
  });
  return map;
}

function listEventDayKeys(event) {
  const startKey = toDateKey(event?.event_date || event?.date);
  const endKey = toDateKey(event?.end_date || event?.event_date || event?.date) || startKey;
  if (!startKey) return [];
  const keys = [];
  const cursor = moment(startKey, 'YYYY-MM-DD');
  const last = moment(endKey, 'YYYY-MM-DD');
  if (!cursor.isValid()) return [];
  while (cursor.isSameOrBefore(last.isValid() ? last : cursor, 'day')) {
    const key = cursor.format('YYYY-MM-DD');
    if (getEventDaySegment(event, key)) keys.push(key);
    cursor.add(1, 'day');
  }
  return keys;
}

/** True when timed footprints on the same day overlap. Touching ends share a lane. */
function daySegmentsConflict(a, b) {
  if (!a || !b) return false;
  return a.segStart < b.segEnd && b.segStart < a.segEnd;
}

/**
 * Assign a stable lane index per event so multi-day bars stay on the same row
 * across date columns. Sequential bookings on the same day (no time overlap)
 * share a lane instead of stacking.
 */
function assignEventLanes(events = []) {
  const sorted = [...events].sort((a, b) => {
    const aStart = toDateKey(a.event_date || a.date) || '';
    const bStart = toDateKey(b.event_date || b.date) || '';
    if (aStart !== bStart) return aStart.localeCompare(bStart);
    const aSeg = timeToMinutes(a.start_time);
    const bSeg = timeToMinutes(b.start_time);
    if (aSeg !== bSeg) return aSeg - bSeg;
    const aDays = listEventDayKeys(a).length;
    const bDays = listEventDayKeys(b).length;
    if (aDays !== bDays) return bDays - aDays; // longer spans first
    return Number(a.id || 0) - Number(b.id || 0);
  });

  // dayKey -> Map(lane -> [{ segStart, segEnd }, ...])
  const occupied = {};
  const laneByEventId = {};

  sorted.forEach((event) => {
    if (event?.id == null) return;
    const dayKeys = listEventDayKeys(event);
    if (!dayKeys.length) return;

    const segmentsByDay = {};
    dayKeys.forEach((dayKey) => {
      const seg = getEventDaySegment(event, dayKey);
      if (seg) {
        segmentsByDay[dayKey] = { segStart: seg.segStart, segEnd: seg.segEnd };
      }
    });

    let lane = 0;
    while (true) {
      const conflict = dayKeys.some((dayKey) => {
        const seg = segmentsByDay[dayKey];
        if (!seg) return false;
        const laneSegs = occupied[dayKey]?.get(lane);
        if (!laneSegs?.length) return false;
        return laneSegs.some((other) => daySegmentsConflict(seg, other));
      });
      if (!conflict) break;
      lane += 1;
    }

    laneByEventId[String(event.id)] = lane;
    dayKeys.forEach((dayKey) => {
      const seg = segmentsByDay[dayKey];
      if (!seg) return;
      if (!occupied[dayKey]) occupied[dayKey] = new Map();
      if (!occupied[dayKey].has(lane)) occupied[dayKey].set(lane, []);
      occupied[dayKey].get(lane).push(seg);
    });
  });

  return laneByEventId;
}

function buildDayLaneSlots(dayEvents = [], laneByEventId = {}, maxLanes = 3, { trimTrailing = true } = {}) {
  // Each lane holds every non-overlapping event for that day (same row).
  const slots = Array.from({ length: maxLanes }, () => []);
  const sorted = [...dayEvents].sort((a, b) => {
    const laneA = laneByEventId[String(a.id)] ?? 999;
    const laneB = laneByEventId[String(b.id)] ?? 999;
    if (laneA !== laneB) return laneA - laneB;
    return Number(a.id || 0) - Number(b.id || 0);
  });

  let overflow = 0;
  sorted.forEach((event) => {
    const lane = laneByEventId[String(event.id)];
    if (lane == null) return;
    if (lane < maxLanes) {
      slots[lane].push(event);
    } else {
      overflow += 1;
    }
  });

  if (!trimTrailing) {
    return { slots, overflow };
  }

  // Keep holes between used lanes (for row alignment), trim only unused trailing rows.
  let lastUsed = -1;
  for (let i = 0; i < slots.length; i += 1) {
    if (slots[i]?.length) lastUsed = i;
  }
  const visibleSlots = lastUsed >= 0 ? slots.slice(0, lastUsed + 1) : [];
  return { slots: visibleSlots, overflow };
}

function HallCircleAvatar({
  src = '',
  name = '',
  size = 20,
  isDark = false,
  accent = HALL_ACCENT,
  borderColor,
}) {
  const initial = String(name || 'H').trim().slice(0, 1).toUpperCase() || 'H';
  return (
    <Avatar
      src={src || undefined}
      alt={name || 'Hall'}
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        fontSize: Math.max(9, Math.round(size * 0.42)),
        fontWeight: 900,
        color: isDark ? '#04120e' : '#0f172a',
        bgcolor: isDark ? alpha(accent, 0.85) : alpha(accent, 0.55),
        border: `1.5px solid ${borderColor || (isDark ? alpha('#fff', 0.75) : alpha('#fff', 0.95))}`,
        boxShadow: `0 2px 6px ${alpha('#000', 0.22)}`,
      }}
    >
      {src ? null : (
        size >= 28
          ? <MapsHomeWorkIcon sx={{ fontSize: Math.round(size * 0.48), opacity: 0.9 }} />
          : initial
      )}
    </Avatar>
  );
}

function resolveEventBookingType(row, startTime, endTime, eventDate, endDate) {
  const parsedNotes = parseHallEventBookingNotes(row?.notes);
  if (parsedNotes.booking_type === 'day' || parsedNotes.booking_type === 'hourly') {
    return parsedNotes.booking_type;
  }
  const start = moment(`${eventDate} ${startTime || '00:00'}`, 'YYYY-MM-DD HH:mm');
  const end = moment(`${endDate || eventDate} ${endTime || '00:00'}`, 'YYYY-MM-DD HH:mm');
  const totalMinutes = start.isValid() && end.isValid() ? end.diff(start, 'minutes') : 0;
  if (endDate && endDate !== eventDate) return 'day';
  if (totalMinutes >= 10 * 60) return 'day';
  return 'hourly';
}

function normalizeCalendarEvent(row) {
  try {
    const startRaw = String(row.start_time || '').slice(0, 8);
    const endRaw = String(row.end_time || '').slice(0, 8);
    const eventDate = toDateKey(row.event_date || row.date);
    const endDate = toDateKey(row.end_date) || eventDate;
    const startTime = startRaw.length >= 5 ? startRaw.slice(0, 5) : (startRaw || '00:00');
    const endTime = endRaw.length >= 5 ? endRaw.slice(0, 5) : (endRaw || '00:00');
    const bookingType = resolveEventBookingType(row, startTime, endTime, eventDate, endDate);
    const durationHours = getEventDurationHours({
      event_date: eventDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,
    });
    return {
      id: row.id,
      event_id: row.event_id || row.booking_code || `EVT-${row.id}`,
      booking_code: row.booking_code || '',
      event_name: row.event_name || 'Untitled event',
      customer: row.customer_name || row.customer || 'Guest',
      customer_name: row.customer_name || row.customer || 'Guest',
      customer_phone: row.customer_phone || '',
      customer_email: row.customer_email || '',
      hall: row.hall_name || (typeof row.hall === 'string' ? row.hall : '') || '—',
      hall_id: row.hall_id || (typeof row.hall === 'object' ? row.hall?.id : row.hall) || null,
      hall_image_url: row.hall_image_url || row.hall_thumbnail || resolveHallImageUrl(row.hall) || '',
      date: eventDate,
      event_date: eventDate,
      end_date: endDate,
      start_time: startTime,
      end_time: endTime,
      booking_type: bookingType,
      duration_hours: durationHours,
      day_blocks: getFullDayBlockCount({ event_date: eventDate, end_date: endDate, booking_type: bookingType }),
      guests: row.guests || 0,
      amount: row.amount || 0,
      status: row.status || 'tentative',
      notes: row.notes || '',
      updated_at: row.updated_at || null,
    };
  } catch (_err) {
    const eventDate = toDateKey(row?.event_date || row?.date) || moment().format('YYYY-MM-DD');
    return {
      id: row?.id,
      event_id: row?.event_id || row?.booking_code || `EVT-${row?.id}`,
      booking_code: row?.booking_code || '',
      event_name: row?.event_name || 'Untitled event',
      customer: row?.customer_name || row?.customer || 'Guest',
      customer_name: row?.customer_name || row?.customer || 'Guest',
      customer_phone: row?.customer_phone || '',
      customer_email: row?.customer_email || '',
      hall: row?.hall_name || (typeof row?.hall === 'string' ? row.hall : '') || '—',
      hall_id: row?.hall_id || (typeof row?.hall === 'object' ? row?.hall?.id : row?.hall) || null,
      hall_image_url: row?.hall_image_url || row?.hall_thumbnail || '',
      date: eventDate,
      event_date: eventDate,
      end_date: toDateKey(row?.end_date) || eventDate,
      start_time: String(row?.start_time || '00:00').slice(0, 5),
      end_time: String(row?.end_time || '00:00').slice(0, 5),
      booking_type: 'hourly',
      duration_hours: 1,
      day_blocks: 1,
      guests: row?.guests || 0,
      amount: row?.amount || 0,
      status: row?.status || 'tentative',
      notes: row?.notes || '',
      updated_at: row?.updated_at || null,
    };
  }
}

function getStatusColor(status) {
  return STATUS_COLORS[String(status || '').toLowerCase()] || MUTED;
}

function buildMonthCells(anchorMoment) {
  const start = anchorMoment.clone().startOf('month').startOf('week');
  const end = anchorMoment.clone().endOf('month').endOf('week');
  const cells = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, 'day')) {
    cells.push(cursor.clone());
    cursor.add(1, 'day');
  }
  return cells;
}

function EventBar({
  event,
  dayKey = '',
  dense = false,
  flush = false,
  onClick,
  isDark = false,
}) {
  const statusColor = getStatusColor(event.status);
  const hallColor = getHallIndicatorColor(event.hall_id, event.hall);
  const segment = dayKey ? getEventDaySegment(event, dayKey) : {
    leftPct: 0,
    widthPct: 100,
    isPartial: false,
    isStartDay: true,
    isEndDay: true,
    continuesLeft: false,
    continuesRight: false,
  };
  if (dayKey && !segment) return null;

  const continuesLeft = Boolean(segment?.continuesLeft);
  const continuesRight = Boolean(segment?.continuesRight);
  const showHallAvatar = Boolean(segment?.isStartDay);
  const avatarSize = dense ? 26 : 30;
  const barHeight = dense ? 36 : 42;
  const leftPct = segment.leftPct || 0;
  // When the event continues into the next day, always reach the cell's right edge.
  const widthPct = continuesRight
    ? Math.max(segment.widthPct || 0, 100 - leftPct)
    : (segment.widthPct || 100);
  const radius = dense ? '12px' : '14px';
  // Opaque fills so a 1px join overlap never looks darker.
  const fillColor = isDark
    ? alpha(hallColor, 0.88)
    : `color-mix(in srgb, ${hallColor} 36%, #ffffff)`;
  const fillHover = isDark
    ? hallColor
    : `color-mix(in srgb, ${hallColor} 48%, #ffffff)`;
  const borderTone = isDark ? alpha('#ffffff', 0.45) : alpha(hallColor, 0.4);
  const hallInitial = String(event.hall || 'H').trim().slice(0, 1).toUpperCase() || 'H';
  // 1px overlap closes sub-pixel gaps at the date edge without a visible seam.
  const joinOverlap = (continuesLeft || continuesRight) ? 1 : 0;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        mb: flush ? 0 : 0.55,
        minHeight: barHeight,
        height: flush ? '100%' : undefined,
        zIndex: continuesLeft || continuesRight ? 4 : 2,
        overflow: 'visible',
      }}
      title={`${event.event_name} · ${event.hall}${segment?.timeLabel ? ` · ${segment.timeLabel}` : ''}`}
    >
      {/* Soft track for same-day timed partials only */}
      {segment?.isPartial && !continuesLeft && !continuesRight ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: radius,
            bgcolor: isDark ? alpha(hallColor, 0.12) : alpha(hallColor, 0.08),
            border: `1px dashed ${isDark ? alpha(hallColor, 0.35) : alpha(hallColor, 0.28)}`,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      <Box
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e, event);
        }}
        sx={{
          position: 'relative',
          ml: continuesLeft && leftPct === 0
            ? `-${joinOverlap}px`
            : `${leftPct}%`,
          width: joinOverlap
            ? `calc(${widthPct}% + ${joinOverlap}px)`
            : `${widthPct}%`,
          pl: 0,
          pr: dense ? 1 : 1.15,
          minHeight: barHeight,
          height: barHeight,
          display: 'flex',
          alignItems: 'center',
          gap: 0.65,
          boxSizing: 'border-box',
          borderTopLeftRadius: continuesLeft ? 0 : radius,
          borderBottomLeftRadius: continuesLeft ? 0 : radius,
          borderTopRightRadius: continuesRight ? 0 : radius,
          borderBottomRightRadius: continuesRight ? 0 : radius,
          borderStyle: 'solid',
          borderColor: borderTone,
          borderTopWidth: 1,
          borderBottomWidth: 1,
          borderLeftWidth: continuesLeft ? 0 : 1,
          borderRightWidth: continuesRight ? 0 : 1,
          bgcolor: fillColor,
          color: isDark ? '#0b1220' : '#0f172a',
          cursor: 'pointer',
          overflow: 'hidden',
          fontSize: dense ? '0.84rem' : '0.9rem',
          fontWeight: 900,
          letterSpacing: '0.01em',
          lineHeight: 1.25,
          boxShadow: 'none',
          transition: 'filter 0.15s ease, background-color 0.15s ease',
          '&:hover': {
            filter: 'brightness(1.02)',
            bgcolor: fillHover,
          },
        }}
      >
        {/* Hall color indicator rail */}
        {!continuesLeft ? (
          <Box
            sx={{
              width: 5,
              alignSelf: 'stretch',
              flexShrink: 0,
              borderTopLeftRadius: radius,
              borderBottomLeftRadius: radius,
              bgcolor: hallColor,
              boxShadow: `inset 0 0 0 1px ${alpha('#000', 0.08)}`,
            }}
          />
        ) : (
          <Box sx={{ width: dense ? 0.75 : 1, flexShrink: 0 }} />
        )}

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.65}
          sx={{
            minWidth: 0,
            flex: 1,
            py: dense ? 0.35 : 0.4,
            position: 'relative',
            zIndex: 1,
            overflow: 'hidden',
          }}
        >
          {showHallAvatar ? (
            <HallCircleAvatar
              src={event.hall_image_url}
              name={event.hall}
              size={avatarSize}
              isDark={isDark}
              accent={hallColor}
              borderColor={alpha(hallColor, 0.95)}
            />
          ) : (
            <Box
              sx={{
                width: dense ? 10 : 12,
                height: dense ? 10 : 12,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: hallColor,
                border: `1.5px solid ${isDark ? alpha('#fff', 0.75) : '#fff'}`,
                boxShadow: `0 0 0 1px ${alpha(hallColor, 0.35)}`,
              }}
            />
          )}
          <Box
            component="span"
            sx={{
              px: 0.55,
              py: 0.1,
              borderRadius: '6px',
              flexShrink: 0,
              bgcolor: alpha(hallColor, isDark ? 0.35 : 0.22),
              color: isDark ? '#04120e' : '#0f172a',
              fontSize: dense ? '0.68rem' : '0.72rem',
              fontWeight: 950,
              lineHeight: 1.2,
              border: `1px solid ${alpha(hallColor, 0.55)}`,
            }}
          >
            {hallInitial}
          </Box>
          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {continuesLeft ? '→ ' : ''}{String(event.event_name || '').toUpperCase()}
          </Box>
          {!continuesLeft && !continuesRight ? (
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                flexShrink: 0,
                ml: 'auto',
                bgcolor: statusColor,
                border: `1px solid ${alpha('#fff', 0.85)}`,
              }}
              title={event.status}
            />
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}

/** One calendar lane row: multiple non-overlapping events share the same vertical slot. */
function LaneEventsRow({
  events = [],
  dayKey,
  dense = false,
  isDark = false,
  onClick,
  showTimeLabels = false,
  subtleText,
}) {
  const barHeight = dense ? 36 : 42;
  const visible = (events || []).filter((event) => getEventDaySegment(event, dayKey));
  if (!visible.length) {
    return (
      <Box sx={{ width: '100%', height: barHeight, mb: 0.55 }} />
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: barHeight,
        mb: 0.55,
        zIndex: 2,
      }}
    >
      {visible.map((event) => (
        <Box
          key={event.id}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            height: barHeight,
          }}
        >
          <EventBar
            event={event}
            dayKey={dayKey}
            dense={dense}
            flush
            isDark={isDark}
            onClick={onClick}
          />
        </Box>
      ))}
      {showTimeLabels ? visible.map((event) => {
        const segment = getEventDaySegment(event, dayKey);
        if (!segment?.timeLabel) return null;
        return (
          <Typography
            key={`time-${event.id}`}
            sx={{
              position: 'absolute',
              top: barHeight + 2,
              left: 0,
              color: subtleText,
              fontSize: '0.65rem',
              fontWeight: 700,
              px: 0.5,
              ml: `${segment.leftPct}%`,
              width: `${
                segment.continuesRight
                  ? Math.max(segment.widthPct || 0, 100 - (segment.leftPct || 0))
                  : (segment.widthPct || 100)
              }%`,
              boxSizing: 'border-box',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              pointerEvents: 'none',
            }}
          >
            {segment.timeLabel}
          </Typography>
        );
      }) : null}
      {showTimeLabels ? <Box sx={{ height: 16 }} /> : null}
    </Box>
  );
}

function DetailRow({ label, value, highlight = false, isDark }) {
  const isNode = value != null && typeof value === 'object';
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems={isNode ? 'center' : 'flex-start'}
      sx={{
        py: 0.7,
        px: 1,
        borderRadius: 1,
        bgcolor: highlight ? alpha(WARNING, isDark ? 0.16 : 0.18) : 'transparent',
      }}
    >
      <Typography sx={{ minWidth: 110, color: SECONDARY, fontWeight: 800, fontSize: '0.78rem' }}>
        {label}
      </Typography>
      {isNode ? (
        value
      ) : (
        <Typography sx={{ color: isDark ? '#e2e8f0' : '#334155', fontWeight: 700, fontSize: '0.78rem' }}>
          {value || '—'}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * Month / Week / Day hall booking calendar (reference-style grid + event popover).
 */
export default function HallBookingCalendar({
  isDark,
  tokens,
  surfaceSx,
  halls = [],
  selectedHallId,
  onSelectHall,
  selectedDate,
  onSelectDate,
  formatCurrency,
  onOpenBookingForDate,
  onOpenBookingEvent,
  onAddHall,
  refreshKey = 0,
}) {
  const [view, setView] = useState('month');
  const [anchorDate, setAnchorDate] = useState(() => moment(selectedDate || undefined));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [popover, setPopover] = useState({ anchorEl: null, event: null });
  const [addPopover, setAddPopover] = useState({
    anchorEl: null,
    date: null,
    hour: '10:00',
  });

  useEffect(() => {
    if (selectedDate) {
      setAnchorDate(moment(selectedDate));
    }
  }, [selectedDate]);

  const range = useMemo(() => {
    if (view === 'day') {
      return {
        start: anchorDate.clone().startOf('day'),
        end: anchorDate.clone().endOf('day'),
      };
    }
    if (view === 'week') {
      return {
        start: anchorDate.clone().startOf('week'),
        end: anchorDate.clone().endOf('week'),
      };
    }
    return {
      start: anchorDate.clone().startOf('month').startOf('week'),
      end: anchorDate.clone().endOf('month').endOf('week'),
    };
  }, [anchorDate, view]);

  const rangeStartKey = range.start.format('YYYY-MM-DD');
  const rangeEndKey = range.end.format('YYYY-MM-DD');

  const hallImageById = useMemo(() => buildHallImageLookup(halls), [halls]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const payload = await getHallBookings({
          start_date: rangeStartKey,
          end_date: rangeEndKey,
          hall_id: selectedHallId || undefined,
          page: 1,
          page_size: 500,
        });
        if (!cancelled) {
          const rows = Array.isArray(payload?.results) ? payload.results : [];
          setEvents(rows.map((row) => normalizeCalendarEvent(row)).filter((row) => row?.id != null));
        }
      } catch (_err) {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [rangeStartKey, rangeEndKey, selectedHallId, refreshKey]);

  const enrichedEvents = useMemo(
    () => events.map((event) => {
      const hallId = event?.hall_id != null ? String(event.hall_id) : '';
      const fromLookup = hallId ? (hallImageById[hallId] || '') : '';
      return {
        ...event,
        hall_image_url: event.hall_image_url || fromLookup || '',
      };
    }),
    [events, hallImageById],
  );

  const eventsByDate = useMemo(() => {
    const map = {};
    enrichedEvents.forEach((event) => {
      const startKey = toDateKey(event.event_date || event.date);
      const endKey = toDateKey(event.end_date || event.event_date || event.date) || startKey;
      if (!startKey) return;
      const cursor = moment(startKey, 'YYYY-MM-DD');
      const last = moment(endKey, 'YYYY-MM-DD');
      if (!cursor.isValid()) return;
      while (cursor.isSameOrBefore(last.isValid() ? last : cursor, 'day')) {
        const key = cursor.format('YYYY-MM-DD');
        // Skip dates with no timed footprint (e.g. ends exactly at midnight).
        if (!getEventDaySegment(event, key)) {
          cursor.add(1, 'day');
          continue;
        }
        if (!map[key]) map[key] = [];
        map[key].push(event);
        cursor.add(1, 'day');
      }
    });
    Object.entries(map).forEach(([dayKey, list]) => {
      list.sort((a, b) => {
        const aStart = getEventDaySegment(a, dayKey)?.segStart ?? timeToMinutes(a.start_time);
        const bStart = getEventDaySegment(b, dayKey)?.segStart ?? timeToMinutes(b.start_time);
        return aStart - bStart;
      });
    });
    return map;
  }, [enrichedEvents]);

  const laneByEventId = useMemo(
    () => assignEventLanes(enrichedEvents),
    [enrichedEvents],
  );

  const monthCells = useMemo(() => buildMonthCells(anchorDate), [anchorDate]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => range.start.clone().add(index, 'day')),
    [range.start],
  );
  const weekLaneCount = useMemo(() => {
    let maxLane = 0;
    weekDays.forEach((day) => {
      const key = day.format('YYYY-MM-DD');
      (eventsByDate[key] || []).forEach((event) => {
        maxLane = Math.max(maxLane, (laneByEventId[String(event.id)] ?? 0) + 1);
      });
    });
    return Math.max(maxLane, 1);
  }, [eventsByDate, laneByEventId, weekDays]);

  /** Fixed lane slots per weekday so multi-day bars stay on one shared row. */
  const weekSlotsByDay = useMemo(() => {
    const map = {};
    weekDays.forEach((day) => {
      const key = day.format('YYYY-MM-DD');
      map[key] = buildDayLaneSlots(
        eventsByDate[key] || [],
        laneByEventId,
        weekLaneCount,
        { trimTrailing: false },
      ).slots;
    });
    return map;
  }, [eventsByDate, laneByEventId, weekDays, weekLaneCount]);

  const dayHours = useMemo(
    () => Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`),
    [],
  );

  const titleLabel = useMemo(() => {
    if (view === 'day') return anchorDate.format('dddd, MMMM D, YYYY');
    if (view === 'week') {
      return `${range.start.format('MMM D')} – ${range.end.format('MMM D, YYYY')}`;
    }
    return anchorDate.format('MMMM, YYYY');
  }, [anchorDate, range.end, range.start, view]);

  const shiftAnchor = (unit, amount) => {
    const next = anchorDate.clone().add(amount, unit);
    setAnchorDate(next);
    onSelectDate?.(next.format('YYYY-MM-DD'));
  };

  const goToday = () => {
    const today = moment();
    setAnchorDate(today);
    onSelectDate?.(today.format('YYYY-MM-DD'));
  };

  const openEventPopover = (mouseEvent, event) => {
    setAddPopover({ anchorEl: null, date: null, hour: '10:00' });
    setPopover({ anchorEl: mouseEvent.currentTarget, event });
  };

  const closePopover = () => setPopover({ anchorEl: null, event: null });

  const closeAddPopover = () => setAddPopover({ anchorEl: null, date: null, hour: '10:00' });

  const openAddPopover = (mouseEvent, dayMoment, hour = '10:00') => {
    mouseEvent?.stopPropagation?.();
    closePopover();
    const dateStr = dayMoment.format('YYYY-MM-DD');
    setAnchorDate(dayMoment.clone());
    onSelectDate?.(dateStr);
    setAddPopover({
      anchorEl: mouseEvent.currentTarget,
      date: dateStr,
      hour: hour || '10:00',
    });
  };

  const chooseBookingType = (bookingType) => {
    if (!addPopover.date) return;
    onOpenBookingForDate?.(addPopover.date, addPopover.hour || '10:00', { bookingType });
    closeAddPopover();
  };

  const handleDayClick = (mouseEvent, dayMoment) => {
    openAddPopover(mouseEvent, dayMoment, '10:00');
  };

  const navUnit = view === 'month' ? 'month' : view === 'week' ? 'week' : 'day';

  const navBtnSx = {
    width: 40,
    height: 40,
    borderRadius: '14px',
    border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
    bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#fff', 0.72),
    color: tokens.text,
    boxShadow: isDark ? 'none' : `0 6px 14px ${alpha('#0f172a', 0.06)}`,
    transition: 'transform 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease',
    '&:hover': {
      bgcolor: alpha(HALL_ACCENT, isDark ? 0.16 : 0.14),
      color: isDark ? HALL_ACCENT : '#0f766e',
      transform: 'translateY(-1px)',
      boxShadow: `0 10px 18px ${alpha(HALL_ACCENT, 0.18)}`,
    },
  };

  const toolbar = (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '22px',
        p: { xs: 1.35, md: 1.6 },
        background: isDark
          ? `linear-gradient(135deg, ${alpha('#0b1c2e', 0.98)} 0%, ${alpha('#102033', 0.96)} 48%, ${alpha('#0d2430', 0.98)} 100%)`
          : `linear-gradient(135deg, #f7fbff 0%, #eef8f4 46%, #f4faff 100%)`,
        border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06)}`,
        boxShadow: isDark
          ? `0 18px 40px ${alpha('#000', 0.28)}, inset 0 1px 0 ${alpha('#fff', 0.05)}`
          : `0 16px 34px ${alpha('#5b9cf5', 0.1)}, inset 0 1px 0 ${alpha('#fff', 0.9)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at 12% 0%, ${alpha(HALL_ACCENT, isDark ? 0.22 : 0.18)} 0%, transparent 34%),
            radial-gradient(circle at 88% 0%, ${alpha(SECONDARY, isDark ? 0.2 : 0.16)} 0%, transparent 32%)
          `,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${HALL_ACCENT}, ${SECONDARY}, ${HALL_ACCENT})`,
          backgroundSize: '200% 100%',
          animation: `${toolbarShimmer} 5.5s ease infinite`,
          pointerEvents: 'none',
        },
      }}
    >
      <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <IconButton size="small" onClick={() => shiftAnchor(navUnit, -1)} sx={navBtnSx}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Button
                onClick={goToday}
                startIcon={<TodayRoundedIcon />}
                sx={{
                  height: 40,
                  px: 1.6,
                  borderRadius: '14px',
                  textTransform: 'none',
                  fontWeight: 900,
                  color: isDark ? HALL_ACCENT : '#0f766e',
                  bgcolor: alpha(HALL_ACCENT, isDark ? 0.14 : 0.16),
                  border: `1px solid ${alpha(HALL_ACCENT, 0.35)}`,
                  boxShadow: `0 8px 18px ${alpha(HALL_ACCENT, 0.12)}`,
                  '&:hover': {
                    bgcolor: alpha(HALL_ACCENT, isDark ? 0.22 : 0.24),
                  },
                }}
              >
                Today
              </Button>
              <IconButton size="small" onClick={() => shiftAnchor(navUnit, 1)} sx={navBtnSx}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: tokens.subtleText,
                  fontWeight: 800,
                  fontSize: '0.68rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  mb: 0.15,
                }}
              >
                Booking Calendar
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: tokens.text,
                  fontWeight: 950,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.15,
                  fontSize: { xs: '1.35rem', md: '1.55rem' },
                }}
              >
                {titleLabel}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'space-between', lg: 'flex-end' }}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 0.4,
                borderRadius: '16px',
                bgcolor: isDark ? alpha('#000', 0.28) : alpha('#fff', 0.7),
                border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06)}`,
                boxShadow: isDark ? 'none' : `inset 0 1px 0 ${alpha('#fff', 0.8)}`,
                gap: 0.35,
              }}
            >
              {VIEW_OPTIONS.map(({ value, label, icon: Icon }) => {
                const active = view === value;
                return (
                  <Button
                    key={value}
                    onClick={() => setView(value)}
                    startIcon={<Icon sx={{ fontSize: 16 }} />}
                    sx={{
                      minWidth: 0,
                      px: 1.35,
                      py: 0.7,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: 900,
                      fontSize: '0.78rem',
                      color: active ? '#031512' : tokens.subtleText,
                      bgcolor: active ? HALL_ACCENT : 'transparent',
                      boxShadow: active ? `0 8px 16px ${alpha(HALL_ACCENT, 0.28)}` : 'none',
                      transition: 'transform 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease',
                      '&:hover': {
                        bgcolor: active ? HALL_ACCENT : alpha(HALL_ACCENT, isDark ? 0.1 : 0.1),
                        color: active ? '#031512' : tokens.text,
                      },
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Box>

            <IconButton
              size="small"
              onClick={() => setFilterOpen((open) => !open)}
              sx={{
                width: 42,
                height: 42,
                borderRadius: '14px',
                color: filterOpen ? '#031512' : SECONDARY,
                bgcolor: filterOpen ? SECONDARY : alpha(SECONDARY, isDark ? 0.14 : 0.1),
                border: `1px solid ${alpha(SECONDARY, filterOpen ? 0.55 : 0.28)}`,
                boxShadow: filterOpen ? `0 10px 18px ${alpha(SECONDARY, 0.28)}` : 'none',
                transition: 'transform 0.18s ease, background-color 0.18s ease',
                '&:hover': {
                  bgcolor: filterOpen ? alpha(SECONDARY, 0.92) : alpha(SECONDARY, 0.2),
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <FilterListRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {filterOpen ? (
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{
              p: 1,
              borderRadius: '16px',
              bgcolor: isDark ? alpha('#000', 0.22) : alpha('#fff', 0.55),
              border: `1px solid ${isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.05)}`,
            }}
          >
            <Chip
              clickable
              label="All Halls"
              onClick={() => onSelectHall?.('')}
              sx={{
                fontWeight: 850,
                borderRadius: '12px',
                color: !selectedHallId ? '#031512' : tokens.text,
                bgcolor: !selectedHallId ? HALL_ACCENT : alpha(tokens.text, 0.05),
                border: `1px solid ${alpha(HALL_ACCENT, !selectedHallId ? 0.5 : 0.15)}`,
              }}
            />
            {halls.map((hall) => {
              const active = String(hall.id) === String(selectedHallId || '');
              const hallColor = getHallIndicatorColor(hall.id, hall.name);
              return (
                <Chip
                  key={hall.id}
                  clickable
                  label={hall.name}
                  onClick={() => onSelectHall?.(String(hall.id))}
                  icon={(
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: hallColor,
                        ml: '10px !important',
                        boxShadow: `0 0 0 2px ${alpha(hallColor, 0.28)}`,
                      }}
                    />
                  )}
                  sx={{
                    fontWeight: 850,
                    borderRadius: '12px',
                    color: active ? '#031512' : tokens.text,
                    bgcolor: active ? alpha(hallColor, 0.55) : alpha(hallColor, isDark ? 0.14 : 0.1),
                    border: `1px solid ${alpha(hallColor, active ? 0.75 : 0.35)}`,
                    '& .MuiChip-icon': { mr: '-2px' },
                  }}
                />
              );
            })}
            {!halls.length ? (
              <Button size="small" onClick={onAddHall} sx={{ fontWeight: 800, color: HALL_ACCENT, textTransform: 'none' }}>
                Add Hall
              </Button>
            ) : null}
          </Stack>
        ) : null}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={1}
          alignItems={{ sm: 'center' }}
        >
          <Stack direction="row" spacing={0.85} flexWrap="wrap" useFlexGap alignItems="center">
            {halls.slice(0, 6).map((hall) => {
              const hallColor = getHallIndicatorColor(hall.id, hall.name);
              return (
                <Chip
                  key={`legend-${hall.id}`}
                  size="small"
                  label={hall.name}
                  icon={(
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        bgcolor: hallColor,
                        boxShadow: `0 0 0 2px ${alpha(hallColor, 0.25)}`,
                        ml: '8px !important',
                      }}
                    />
                  )}
                  sx={{
                    height: 28,
                    px: 0.35,
                    borderRadius: '999px',
                    fontWeight: 850,
                    maxWidth: 160,
                    color: isDark ? alpha('#fff', 0.92) : '#0f172a',
                    bgcolor: alpha(hallColor, isDark ? 0.16 : 0.12),
                    border: `1px solid ${alpha(hallColor, 0.35)}`,
                    '& .MuiChip-label': {
                      px: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                    '& .MuiChip-icon': { marginRight: '-2px' },
                  }}
                />
              );
            })}
            {Object.entries(STATUS_COLORS).map(([status, color]) => (
              <Chip
                key={status}
                size="small"
                label={status}
                icon={(
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: color,
                      boxShadow: `0 0 0 3px ${alpha(color, 0.22)}`,
                      ml: '8px !important',
                    }}
                  />
                )}
                sx={{
                  height: 28,
                  px: 0.35,
                  borderRadius: '999px',
                  textTransform: 'capitalize',
                  fontWeight: 850,
                  color: isDark ? alpha('#fff', 0.92) : '#0f172a',
                  bgcolor: alpha(color, isDark ? 0.16 : 0.14),
                  border: `1px solid ${alpha(color, 0.32)}`,
                  '& .MuiChip-label': { px: 1 },
                  '& .MuiChip-icon': { marginRight: '-2px' },
                }}
              />
            ))}
          </Stack>

          {loading ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                px: 1.1,
                py: 0.55,
                borderRadius: '999px',
                bgcolor: alpha(HALL_ACCENT, isDark ? 0.12 : 0.1),
                border: `1px solid ${alpha(HALL_ACCENT, 0.25)}`,
              }}
            >
              <CircularProgress size={14} sx={{ color: HALL_ACCENT }} />
              <Typography variant="caption" sx={{ color: isDark ? HALL_ACCENT : '#0f766e', fontWeight: 800 }}>
                Loading events…
              </Typography>
            </Stack>
          ) : (
            <Typography
              variant="caption"
              sx={{
                color: tokens.subtleText,
                fontWeight: 800,
                px: 1.1,
                py: 0.55,
                borderRadius: '999px',
                bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#0f172a', 0.04),
                border: `1px solid ${isDark ? alpha('#fff', 0.06) : alpha('#0f172a', 0.06)}`,
              }}
            >
              {events.length} event{events.length === 1 ? '' : 's'} in view
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );

  const calendarColLine = `1px solid ${alpha(tokens.text, isDark ? 0.1 : 0.08)}`;

  const monthView = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        border: `1px solid ${alpha(tokens.text, 0.1)}`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: isDark ? alpha('#020617', 0.35) : '#fff',
      }}
    >
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, dayIdx) => (
        <Box
          key={day}
          sx={{
            px: 1,
            py: 1,
            boxSizing: 'border-box',
            minWidth: 0,
            bgcolor: isDark ? alpha('#fff', 0.03) : alpha(SECONDARY, 0.06),
            borderBottom: calendarColLine,
            borderLeft: dayIdx === 0 ? 'none' : calendarColLine,
          }}
        >
          <Typography sx={{ fontWeight: 900, fontSize: '0.75rem', color: tokens.subtleText, textAlign: 'center' }}>
            {day}
          </Typography>
        </Box>
      ))}
      {monthCells.map((day, cellIdx) => {
        const key = day.format('YYYY-MM-DD');
        const inMonth = day.month() === anchorDate.month();
        const isToday = day.isSame(moment(), 'day');
        const dayEvents = eventsByDate[key] || [];
        const { slots, overflow } = buildDayLaneSlots(dayEvents, laneByEventId, 3);
        const isSelected = addPopover.date === key;
        const laneHeight = 36;
        const colIdx = cellIdx % 7;
        return (
          <Box
            key={key}
            onClick={(event) => handleDayClick(event, day)}
            sx={{
              minHeight: { xs: 112, md: 140 },
              minWidth: 0,
              boxSizing: 'border-box',
              pt: 0.75,
              pb: 0.5,
              cursor: 'pointer',
              overflow: 'visible',
              position: 'relative',
              zIndex: 1,
              bgcolor: inMonth
                ? (isDark ? 'transparent' : '#fff')
                : (isDark ? alpha('#fff', 0.02) : alpha('#f1f5f9', 0.9)),
              borderLeft: 'none',
              borderBottom: `1px solid ${alpha(tokens.text, 0.06)}`,
              outline: isSelected ? `2px solid ${SECONDARY}` : 'none',
              outlineOffset: -2,
              // Column line behind events so multi-day bars can cover the join.
              '&::before': colIdx === 0 ? undefined : {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '1px',
                bgcolor: alpha(tokens.text, isDark ? 0.1 : 0.08),
                zIndex: 0,
                pointerEvents: 'none',
              },
              '&:hover': {
                bgcolor: alpha(HALL_ACCENT, isDark ? 0.08 : 0.08),
              },
            }}
          >
            <Stack direction="row" justifyContent="flex-end" sx={{ px: 0.75, mb: 0.55 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 900,
                  color: isToday ? '#031512' : (inMonth ? tokens.text : tokens.subtleText),
                  bgcolor: isToday ? HALL_ACCENT : 'transparent',
                }}
              >
                {day.date()}
              </Box>
            </Stack>
            {/* Full-bleed event lane so multi-day bars meet with no side gap */}
            <Box sx={{ width: '100%', position: 'relative', zIndex: 2 }}>
              {slots.map((laneEvents, laneIdx) => (
                laneEvents?.length ? (
                  <LaneEventsRow
                    key={`lane-${key}-${laneIdx}`}
                    events={laneEvents}
                    dayKey={key}
                    dense
                    isDark={isDark}
                    onClick={openEventPopover}
                  />
                ) : (
                  <Box
                    key={`lane-spacer-${key}-${laneIdx}`}
                    sx={{ width: '100%', height: laneHeight, mb: 0.55 }}
                  />
                )
              ))}
            </Box>
            {overflow > 0 ? (
              <Typography sx={{ color: SECONDARY, fontSize: '0.68rem', fontWeight: 800, px: 0.75 }}>
                +{overflow} more
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );

  const WEEK_LANE_ROW_MIN = 64;

  const weekView = (
    <Box
      sx={{
        border: `1px solid ${alpha(tokens.text, 0.1)}`,
        borderRadius: 2,
        overflow: 'hidden',
        minHeight: 520,
        bgcolor: isDark ? alpha('#020617', 0.35) : '#fff',
      }}
    >
      {/* Day headers — equal 7 columns */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          borderBottom: calendarColLine,
        }}
      >
        {weekDays.map((day, dayIdx) => {
          const key = day.format('YYYY-MM-DD');
          const isToday = day.isSame(moment(), 'day');
          const isSelected = addPopover.date === key;
          return (
            <Box
              key={`week-head-${key}`}
              onClick={(event) => handleDayClick(event, day)}
              sx={{
                minWidth: 0,
                boxSizing: 'border-box',
                px: 1,
                pt: 1,
                pb: 1,
                cursor: 'pointer',
                position: 'relative',
                bgcolor: isToday ? alpha(HALL_ACCENT, 0.06) : 'transparent',
                outline: isSelected ? `2px solid ${SECONDARY}` : 'none',
                outlineOffset: -2,
                borderLeft: dayIdx === 0 ? 'none' : calendarColLine,
                '&:hover': { bgcolor: alpha(SECONDARY, 0.06) },
              }}
            >
              <Stack spacing={0.35}>
                <Typography sx={{ color: tokens.subtleText, fontWeight: 800, fontSize: '0.72rem' }}>
                  {day.format('ddd').toUpperCase()}
                </Typography>
                <Typography
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 950,
                    color: isToday ? '#031512' : tokens.text,
                    bgcolor: isToday ? HALL_ACCENT : 'transparent',
                  }}
                >
                  {day.date()}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Box>

      {/* Shared lane rows — one CSS grid row per lane keeps bars aligned across days */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gridAutoRows: `minmax(${WEEK_LANE_ROW_MIN}px, auto)`,
          alignItems: 'stretch',
          minHeight: 420,
        }}
      >
        {Array.from({ length: weekLaneCount }, (_, laneIdx) => (
          weekDays.map((day, dayIdx) => {
            const key = day.format('YYYY-MM-DD');
            const isToday = day.isSame(moment(), 'day');
            const laneEvents = weekSlotsByDay[key]?.[laneIdx] || [];
            const dayEvents = eventsByDate[key] || [];
            const showFree = !dayEvents.length && laneIdx === 0;

            return (
              <Box
                key={`week-lane-${laneIdx}-${key}`}
                onClick={(mouseEvent) => handleDayClick(mouseEvent, day)}
                sx={{
                  minWidth: 0,
                  boxSizing: 'border-box',
                  position: 'relative',
                  overflow: 'visible',
                  cursor: 'pointer',
                  bgcolor: isToday ? alpha(HALL_ACCENT, 0.04) : 'transparent',
                  borderLeft: dayIdx === 0 ? 'none' : calendarColLine,
                  borderTop: laneIdx === 0 ? 'none' : `1px solid ${alpha(tokens.text, 0.04)}`,
                  py: 0.5,
                  '&:hover': { bgcolor: alpha(SECONDARY, 0.05) },
                }}
              >
                {laneEvents.length ? (
                  <LaneEventsRow
                    events={laneEvents}
                    dayKey={key}
                    isDark={isDark}
                    onClick={openEventPopover}
                    showTimeLabels
                    subtleText={tokens.subtleText}
                  />
                ) : showFree ? (
                  <Typography
                    sx={{
                      color: tokens.subtleText,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      px: 1,
                      pt: 1,
                    }}
                  >
                    Free
                  </Typography>
                ) : null}
              </Box>
            );
          })
        )).flat()}
      </Box>
    </Box>
  );

  const selectedDayKey = toDateKey(anchorDate) || anchorDate.format('YYYY-MM-DD');
  const selectedDayEvents = useMemo(() => {
    const fromMap = eventsByDate[selectedDayKey] || [];
    if (fromMap.length) return fromMap;
    return enrichedEvents.filter((event) => {
      const startKey = toDateKey(event.event_date || event.date);
      const endKey = toDateKey(event.end_date || event.event_date || event.date) || startKey;
      if (!startKey) return false;
      return selectedDayKey >= startKey && selectedDayKey <= endKey;
    });
  }, [enrichedEvents, eventsByDate, selectedDayKey]);
  const DAY_ROW_HEIGHT = 56;

  const dayView = (
    <Box
      sx={{
        display: 'flex',
        border: `1px solid ${alpha(tokens.text, 0.1)}`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: isDark ? alpha('#020617', 0.35) : '#fff',
      }}
    >
      <Box sx={{ width: 72, flexShrink: 0, borderBottom: `1px solid ${alpha(tokens.text, 0.08)}` }}>
        {dayHours.map((hour) => (
          <Typography
            key={`label-${hour}`}
            sx={{
              height: DAY_ROW_HEIGHT,
              px: 1,
              pt: 1.1,
              color: tokens.subtleText,
              fontWeight: 800,
              fontSize: '0.75rem',
              borderBottom: `1px solid ${alpha(tokens.text, 0.07)}`,
              boxSizing: 'border-box',
            }}
          >
            {hour}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          flex: 1,
          position: 'relative',
          minWidth: 0,
          height: DAY_ROW_HEIGHT * 24,
        }}
      >
        {dayHours.map((hour) => {
          const hourBusy = selectedDayEvents.some((event) => eventOverlapsHour(event, selectedDayKey, hour));
          return (
            <Box
              key={`slot-${hour}`}
              onClick={(event) => {
                if (hourBusy) return;
                openAddPopover(event, anchorDate, hour);
              }}
              sx={{
                height: DAY_ROW_HEIGHT,
                borderBottom: `1px solid ${alpha(tokens.text, 0.07)}`,
                boxSizing: 'border-box',
                cursor: hourBusy ? 'default' : 'pointer',
                outline: addPopover.date === selectedDayKey && addPopover.hour === hour
                  ? `2px solid ${SECONDARY}`
                  : 'none',
                outlineOffset: -2,
                px: 1,
                py: 0.75,
                '&:hover': hourBusy ? undefined : { bgcolor: alpha(HALL_ACCENT, 0.05) },
              }}
            >
              {!hourBusy ? (
                <Box
                  sx={{
                    minHeight: 34,
                    borderRadius: 1,
                    border: `1px dashed ${alpha(tokens.text, 0.1)}`,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    color: tokens.subtleText,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                  }}
                >
                  Click to book {hour}
                </Box>
              ) : null}
            </Box>
          );
        })}

        {selectedDayEvents.map((event) => {
          const layout = getDayViewEventLayout(event, selectedDayKey, DAY_ROW_HEIGHT);
          const statusColor = getStatusColor(event.status);
          const hallColor = getHallIndicatorColor(event.hall_id, event.hall);
          const hoursLabel = event.booking_type === 'day'
            ? 'Full Day'
            : `${event.duration_hours || getEventDurationHours(event)}h`;
          return (
            <Box
              key={`day-event-${event.id}`}
              onClick={(e) => {
                e.stopPropagation();
                openEventPopover(e, event);
              }}
              sx={{
                position: 'absolute',
                top: layout.top + 3,
                left: 6,
                right: 8,
                height: Math.max(44, layout.height - 6),
                p: 1,
                pl: 1.25,
                borderRadius: 1.5,
                bgcolor: isDark ? alpha(hallColor, 0.38) : alpha(hallColor, 0.28),
                border: `1.5px solid ${alpha(hallColor, 0.65)}`,
                boxShadow: isDark ? `0 8px 18px ${alpha('#000', 0.28)}` : `0 8px 18px ${alpha(hallColor, 0.2)}`,
                cursor: 'pointer',
                overflow: 'hidden',
                zIndex: 4,
                borderLeft: `5px solid ${hallColor}`,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 10px 22px ${alpha(hallColor, 0.3)}`,
                },
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.35 }}>
                <HallCircleAvatar
                  src={event.hall_image_url}
                  name={event.hall}
                  size={22}
                  isDark={isDark}
                  accent={hallColor}
                  borderColor={alpha(hallColor, 0.95)}
                />
                <Typography sx={{ color: tokens.text, fontWeight: 900, fontSize: '0.85rem', flex: 1, minWidth: 0 }} noWrap>
                  {event.event_name}
                </Typography>
                <Chip
                  size="small"
                  label={hoursLabel}
                  sx={{
                    height: 22,
                    fontWeight: 900,
                    fontSize: '0.65rem',
                    bgcolor: isDark ? alpha(hallColor, 0.95) : hallColor,
                    color: '#0b1220',
                  }}
                />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: statusColor,
                    border: `1px solid ${alpha('#fff', 0.9)}`,
                    flexShrink: 0,
                  }}
                  title={event.status}
                />
              </Stack>
              <Typography sx={{ color: tokens.subtleText, fontSize: '0.72rem', fontWeight: 700 }} noWrap>
                {formatTimeLabel(event.start_time)} – {formatTimeLabel(event.end_time)}
                {' · '}
                {event.booking_type === 'day' ? 'Full Day' : 'Hourly'}
                {' · '}
                {event.hall}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const activeEvent = useMemo(() => {
    if (!popover.event) return null;
    const hallId = popover.event.hall_id != null ? String(popover.event.hall_id) : '';
    return {
      ...popover.event,
      hall_image_url: popover.event.hall_image_url || (hallId ? hallImageById[hallId] : '') || '',
    };
  }, [hallImageById, popover.event]);

  const activeStatusColor = getStatusColor(activeEvent?.status);

  return (
    <Paper
      sx={{
        ...surfaceSx,
        p: { xs: 1.5, md: 2 },
        borderRadius: '24px',
        background: isDark
          ? `linear-gradient(160deg, ${alpha('#071423', 0.99)} 0%, ${alpha('#0f172a', 0.98)} 55%, ${alpha('#0b1f1a', 0.96)} 100%)`
          : 'linear-gradient(160deg, #ffffff 0%, #f7fbff 55%, #f3fbf7 100%)',
        border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.06)}`,
        boxShadow: isDark
          ? `0 22px 48px ${alpha('#000', 0.28)}`
          : `0 18px 40px ${alpha('#5b9cf5', 0.08)}`,
      }}
    >
      <Stack spacing={2}>
        {toolbar}

        <Box
          sx={{
            borderRadius: '18px',
            overflow: 'hidden',
            boxShadow: isDark
              ? `inset 0 1px 0 ${alpha('#fff', 0.04)}`
              : `0 10px 24px ${alpha('#0f172a', 0.05)}`,
          }}
        >
          {view === 'month' ? monthView : view === 'week' ? weekView : dayView}
        </Box>
      </Stack>

      <Popover
        open={Boolean(addPopover.anchorEl && addPopover.date)}
        anchorEl={addPopover.anchorEl}
        onClose={closeAddPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 300,
            p: 1.35,
            borderRadius: '18px',
            border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.08)}`,
            bgcolor: isDark ? '#0b1524' : '#fff',
            backgroundImage: isDark
              ? `linear-gradient(160deg, ${alpha('#102033', 0.98)} 0%, ${alpha('#0b1f1a', 0.96)} 100%)`
              : `linear-gradient(160deg, #ffffff 0%, #f4fbf8 100%)`,
            boxShadow: isDark
              ? `0 20px 44px ${alpha('#000', 0.5)}`
              : `0 18px 40px ${alpha('#0f172a', 0.14)}`,
          },
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1.1} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '12px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(HALL_ACCENT, isDark ? 0.18 : 0.16),
                color: isDark ? HALL_ACCENT : '#0f766e',
                border: `1px solid ${alpha(HALL_ACCENT, 0.35)}`,
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ color: tokens.text, fontWeight: 950, lineHeight: 1.2 }}>
                Add Event
              </Typography>
              <Typography sx={{ color: tokens.subtleText, fontSize: '0.75rem', fontWeight: 700 }}>
                {addPopover.date
                  ? moment(addPopover.date).format('ddd, MMM D')
                  : 'Choose booking type'}
              </Typography>
            </Box>
          </Stack>

          <Typography sx={{ color: tokens.subtleText, fontSize: '0.78rem', fontWeight: 700 }}>
            Use this date to start a booking
          </Typography>

          <Stack spacing={0.85}>
            <Button
              onClick={() => chooseBookingType('hourly')}
              startIcon={<AccessTimeRoundedIcon />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontWeight: 900,
                borderRadius: '12px',
                px: 1.25,
                py: 1,
                color: tokens.text,
                bgcolor: alpha(SECONDARY, isDark ? 0.14 : 0.1),
                border: `1px solid ${alpha(SECONDARY, 0.3)}`,
                '&:hover': { bgcolor: alpha(SECONDARY, isDark ? 0.22 : 0.16) },
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', lineHeight: 1.2 }}>Hourly</Typography>
                <Typography sx={{ color: tokens.subtleText, fontSize: '0.7rem', fontWeight: 700 }}>
                  Book by hour · starts {formatTimeLabel(addPopover.hour || '10:00')}
                </Typography>
              </Box>
            </Button>
            <Button
              onClick={() => chooseBookingType('day')}
              startIcon={<WbSunnyRoundedIcon />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontWeight: 900,
                borderRadius: '12px',
                px: 1.25,
                py: 1,
                color: '#031512',
                bgcolor: HALL_ACCENT,
                border: `1px solid ${alpha(HALL_ACCENT, 0.5)}`,
                boxShadow: `0 10px 18px ${alpha(HALL_ACCENT, 0.22)}`,
                '&:hover': { bgcolor: alpha(HALL_ACCENT, 0.92) },
                '& .MuiButton-startIcon': { color: '#031512' },
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', lineHeight: 1.2, color: '#031512' }}>
                  Full Day
                </Typography>
                <Typography sx={{ color: alpha('#031512', 0.7), fontSize: '0.7rem', fontWeight: 700 }}>
                  Book the full day rate
                </Typography>
              </Box>
            </Button>
          </Stack>
        </Stack>
      </Popover>

      <Popover
        open={Boolean(popover.anchorEl && activeEvent)}
        anchorEl={popover.anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 360,
            p: 0,
            overflow: 'hidden',
            borderRadius: '18px',
            border: `1px solid ${alpha(tokens.text, 0.12)}`,
            bgcolor: isDark ? '#0b1524' : '#fff',
            boxShadow: isDark
              ? `0 22px 48px ${alpha('#000', 0.5)}`
              : `0 22px 48px ${alpha('#0f172a', 0.16)}`,
          },
        }}
      >
        {activeEvent ? (
          <Stack spacing={0}>
            <Box
              sx={{
                px: 1.5,
                pt: 1.35,
                pb: 1.2,
                background: isDark
                  ? `linear-gradient(145deg, ${alpha(activeStatusColor, 0.22)} 0%, ${alpha('#0b1524', 0.2)} 55%, transparent 100%)`
                  : `linear-gradient(145deg, ${alpha(activeStatusColor, 0.2)} 0%, ${alpha('#fff', 0.4)} 55%, transparent 100%)`,
                borderBottom: `1px solid ${alpha(tokens.text, 0.08)}`,
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <HallCircleAvatar
                  src={activeEvent.hall_image_url}
                  name={activeEvent.hall}
                  size={52}
                  isDark={isDark}
                  accent={activeStatusColor}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ color: tokens.subtleText, fontWeight: 800, fontSize: '0.68rem', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Event Details
                  </Typography>
                  <Typography
                    sx={{
                      color: tokens.text,
                      fontWeight: 950,
                      fontSize: '1rem',
                      lineHeight: 1.25,
                      letterSpacing: '-0.01em',
                    }}
                    noWrap
                    title={activeEvent.event_name}
                  >
                    {activeEvent.event_name}
                  </Typography>
                  <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mt: 0.35, minWidth: 0 }}>
                    <MapsHomeWorkIcon sx={{ fontSize: 13, color: tokens.subtleText, flexShrink: 0 }} />
                    <Typography
                      sx={{ color: tokens.subtleText, fontWeight: 750, fontSize: '0.72rem' }}
                      noWrap
                      title={activeEvent.hall}
                    >
                      {activeEvent.hall || 'Venue'}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.65 }} useFlexGap flexWrap="wrap">
                    <Chip
                      size="small"
                      label={String(activeEvent.status || '—')}
                      sx={{
                        height: 22,
                        fontWeight: 900,
                        fontSize: '0.68rem',
                        textTransform: 'capitalize',
                        bgcolor: alpha(activeStatusColor, isDark ? 0.28 : 0.22),
                        color: isDark ? alpha('#fff', 0.95) : '#0f172a',
                        border: `1px solid ${alpha(activeStatusColor, 0.45)}`,
                      }}
                    />
                    <Chip
                      size="small"
                      label={
                        activeEvent.booking_type === 'day'
                          ? `${activeEvent.day_blocks || getFullDayBlockCount(activeEvent)} day${(activeEvent.day_blocks || getFullDayBlockCount(activeEvent)) === 1 ? '' : 's'}`
                          : `${activeEvent.duration_hours || getEventDurationHours(activeEvent)}h`
                      }
                      sx={{
                        height: 22,
                        fontWeight: 900,
                        fontSize: '0.68rem',
                        bgcolor: alpha(SECONDARY, isDark ? 0.22 : 0.14),
                        color: isDark ? '#93c5fd' : '#1d4ed8',
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ px: 0.75, py: 0.6 }}>
            <DetailRow
              label="Event ID"
              value={(
                <Typography
                  component="button"
                  type="button"
                  onClick={() => {
                    closePopover();
                    onOpenBookingEvent?.(activeEvent, 'view');
                  }}
                  sx={{
                    border: 0,
                    background: 'none',
                    p: 0,
                    m: 0,
                    cursor: 'pointer',
                    color: HALL_ACCENT,
                    fontWeight: 900,
                    fontSize: '0.78rem',
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                    '&:hover': { opacity: 0.85 },
                  }}
                >
                  {activeEvent.event_id}
                </Typography>
              )}
              isDark={isDark}
            />
            <DetailRow
              label="Venue"
              value={(
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                  <HallCircleAvatar
                    src={activeEvent.hall_image_url}
                    name={activeEvent.hall}
                    size={24}
                    isDark={isDark}
                    accent={HALL_ACCENT}
                  />
                  <Typography
                    sx={{ color: isDark ? '#e2e8f0' : '#334155', fontWeight: 800, fontSize: '0.78rem' }}
                    noWrap
                    title={activeEvent.hall}
                  >
                    {activeEvent.hall || '—'}
                  </Typography>
                </Stack>
              )}
              isDark={isDark}
            />
            <DetailRow
              label="Schedule"
              value={activeEvent.booking_type === 'day' ? 'Full Day' : 'Hourly'}
              isDark={isDark}
            />
            <DetailRow
              label="Date"
              value={
                activeEvent.end_date && activeEvent.end_date !== activeEvent.event_date
                  ? `${moment(activeEvent.event_date).format('MMM D, YYYY')} → ${moment(activeEvent.end_date).format('MMM D, YYYY')}`
                  : moment(activeEvent.event_date).format('MMM D, YYYY')
              }
              isDark={isDark}
            />
            <DetailRow
              label="Time"
              value={`${formatTimeLabel(activeEvent.start_time)} – ${formatTimeLabel(activeEvent.end_time)}`}
              isDark={isDark}
            />
            <DetailRow
              label="Client"
              value={(
                <Stack direction="row" spacing={0.6} alignItems="center">
                  <PersonOutlineRoundedIcon sx={{ fontSize: 15, color: tokens.subtleText }} />
                  <Typography sx={{ color: isDark ? '#e2e8f0' : '#334155', fontWeight: 700, fontSize: '0.78rem' }}>
                    {activeEvent.customer || '—'}
                  </Typography>
                </Stack>
              )}
              isDark={isDark}
            />
            <DetailRow
              label="Guests"
              value={(
                <Stack direction="row" spacing={0.6} alignItems="center">
                  <GroupsOutlinedIcon sx={{ fontSize: 15, color: tokens.subtleText }} />
                  <Typography sx={{ color: isDark ? '#e2e8f0' : '#334155', fontWeight: 700, fontSize: '0.78rem' }}>
                    {String(activeEvent.guests || 0)}
                  </Typography>
                </Stack>
              )}
              isDark={isDark}
            />
            <DetailRow label="Amount" value={formatCurrency?.(activeEvent.amount)} isDark={isDark} />
            </Box>
            <Stack
              direction="row"
              spacing={1.25}
              sx={{
                px: 1.25,
                pt: 0.5,
                pb: 1.25,
              }}
            >
              <Button
                fullWidth
                disableElevation
                onClick={() => {
                  closePopover();
                  onOpenBookingEvent?.(activeEvent, 'view');
                }}
                startIcon={
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isDark ? alpha(HALL_ACCENT, 0.18) : alpha(HALL_ACCENT, 0.14),
                      color: HALL_ACCENT,
                    }}
                  >
                    <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
                  </Box>
                }
                sx={{
                  flex: 1,
                  py: 1.05,
                  px: 1.5,
                  borderRadius: '14px',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: '0.88rem',
                  letterSpacing: '0.01em',
                  color: isDark ? '#e8edf2' : '#0f172a',
                  bgcolor: isDark ? alpha('#ffffff', 0.06) : alpha('#0f172a', 0.04),
                  border: `1px solid ${isDark ? alpha(HALL_ACCENT, 0.35) : alpha(HALL_ACCENT, 0.28)}`,
                  boxShadow: isDark
                    ? `inset 0 1px 0 ${alpha('#fff', 0.04)}`
                    : `0 4px 12px ${alpha('#0f172a', 0.06)}`,
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease',
                  '& .MuiButton-startIcon': { m: 0, mr: 1 },
                  '&:hover': {
                    bgcolor: isDark ? alpha(HALL_ACCENT, 0.14) : alpha(HALL_ACCENT, 0.1),
                    borderColor: HALL_ACCENT,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 8px 18px ${alpha(HALL_ACCENT, 0.22)}`,
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                View
              </Button>
              {activeEvent.status !== 'cancelled' ? (
                <Button
                  fullWidth
                  disableElevation
                  onClick={() => {
                    closePopover();
                    onOpenBookingEvent?.(activeEvent, 'modify');
                  }}
                  startIcon={
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '10px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha('#031512', 0.12),
                        color: '#031512',
                      }}
                    >
                      <EditRoundedIcon sx={{ fontSize: 16 }} />
                    </Box>
                  }
                  sx={{
                    flex: 1,
                    py: 1.05,
                    px: 1.5,
                    borderRadius: '14px',
                    textTransform: 'none',
                    fontWeight: 900,
                    fontSize: '0.88rem',
                    letterSpacing: '0.01em',
                    color: '#031512',
                    background: `linear-gradient(135deg, ${HALL_ACCENT} 0%, #35c9a0 100%)`,
                    border: 'none',
                    boxShadow: `0 8px 20px ${alpha(HALL_ACCENT, 0.38)}`,
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease',
                    '& .MuiButton-startIcon': { m: 0, mr: 1 },
                    '&:hover': {
                      background: `linear-gradient(135deg, ${HALL_ACCENT} 0%, #2fbe96 100%)`,
                      filter: 'brightness(1.03)',
                      transform: 'translateY(-1px)',
                      boxShadow: `0 12px 26px ${alpha(HALL_ACCENT, 0.48)}`,
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: `0 6px 14px ${alpha(HALL_ACCENT, 0.32)}`,
                    },
                  }}
                >
                  Edit
                </Button>
              ) : null}
            </Stack>
          </Stack>
        ) : null}
      </Popover>
    </Paper>
  );
}
